import React, { useEffect, useMemo, useState } from "react";
import { Users, Shield, ShieldCheck, Smartphone, MessageSquare, Search } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const CONSENT_KEYS = [
  "remember_name",
  "remember_face",
  "remember_voice",
  "remember_devices",
  "remember_conversations",
  "remember_preferences",
  "access_medical_history",
];

export default function PersonPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();

  const [me, setMe] = useState(null);
  const [persons, setPersons] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const [meRes, listRes] = await Promise.all([
        api.get("/persons/me"),
        api.get("/persons"),
      ]);
      setMe(meRes.data);
      setPersons(listRes.data || []);
      setSelectedId((id) => id || meRes.data?.id);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const selected = useMemo(
    () => persons.find((p) => p.id === selectedId) || me,
    [persons, selectedId, me]
  );

  useEffect(() => {
    if (!selectedId) return;
    api.get(`/persons/${selectedId}/encounters`).then((r) => setEncounters(r.data || [])).catch(() => setEncounters([]));
  }, [selectedId]);

  const filtered = persons.filter(
    (p) => !filter || (p.display_name || "").toLowerCase().includes(filter.toLowerCase())
  );

  async function toggleConsent(key, value) {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/persons/${selected.id}/consent`, {
        updates: { [key]: value },
      });
      setPersons((arr) => arr.map((p) => (p.id === data.id ? data : p)));
      if (me?.id === data.id) setMe(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function patchField(field, value) {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/persons/${selected.id}`, { [field]: value });
      setPersons((arr) => arr.map((p) => (p.id === data.id ? data : p)));
      if (me?.id === data.id) setMe(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="person-page">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t.person.eyebrow}
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{t.person.title}</h1>
        <p className="mt-1 text-sm text-slate-500">{t.person.sub}</p>
      </header>

      {error && (
        <div className="mios-card" data-testid="person-error">
          <div className="mios-card-body text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[320px,1fr]">
        {/* List */}
        <aside className="mios-card" data-testid="person-list-card">
          <div className="mios-card-header">
            <div className="mios-card-title flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-400" />
              {t.person.known} <span className="text-slate-400 mono">({persons.length})</span>
            </div>
          </div>
          <div className="mios-card-body">
            <label className="relative mb-3 flex items-center">
              <Search className="absolute left-2.5 h-4 w-4 text-slate-400" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t.person.search}
                className="mios-input pl-8"
                data-testid="person-search-input"
              />
            </label>
            <ul className="space-y-1.5">
              {filtered.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(p.id)}
                    data-testid={`person-row-${p.id}`}
                    className={`w-full rounded-lg px-3 py-2 text-left transition-colors ${
                      selectedId === p.id ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="truncate text-sm font-medium">{p.display_name}</div>
                      <span
                        className={`mios-pill ${
                          p.relationship === "owner"
                            ? "mios-pill-normal"
                            : p.relationship === "clinician"
                            ? "mios-pill-warning"
                            : "mios-pill-low"
                        }`}
                      >
                        {t.person.relationships[p.relationship] || p.relationship}
                      </span>
                    </div>
                    <div className={`mt-0.5 text-[11px] ${selectedId === p.id ? "text-slate-300" : "text-slate-500"}`}>
                      {t.person.encounters}: {p.encounter_count || 0}
                    </div>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="text-sm text-slate-400">{t.person.empty}</li>
              )}
            </ul>
          </div>
        </aside>

        {/* Detail */}
        <section className="space-y-4">
          {selected ? (
            <>
              <div className="mios-card" data-testid="person-detail-card">
                <div className="mios-card-header">
                  <div>
                    <div className="mios-card-sub">{t.person.profile}</div>
                    <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                      {selected.display_name}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                      {t.person.relationships[selected.relationship] || selected.relationship} ·{" "}
                      {selected.language?.toUpperCase()} · {t.person.metSince}{" "}
                      {new Date(selected.first_met_at || selected.created_at).toLocaleDateString(language)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="mios-card-sub">{t.person.encounters}</div>
                    <div className="mono mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                      {selected.encounter_count || 0}
                    </div>
                  </div>
                </div>
                <div className="mios-card-body space-y-3">
                  <Field
                    label={t.person.displayName}
                    value={selected.display_name || ""}
                    onSave={(v) => patchField("display_name", v)}
                    disabled={saving}
                    testid="person-name-field"
                  />
                  <Field
                    label={t.person.tags}
                    value={(selected.tags || []).join(", ")}
                    onSave={(v) =>
                      patchField(
                        "tags",
                        v
                          .split(",")
                          .map((x) => x.trim())
                          .filter(Boolean)
                      )
                    }
                    disabled={saving}
                    testid="person-tags-field"
                  />
                  <Field
                    label={t.person.notes}
                    value={selected.notes || ""}
                    onSave={(v) => patchField("notes", v || null)}
                    disabled={saving}
                    testid="person-notes-field"
                    multiline
                  />
                </div>
              </div>

              <div className="mios-card" data-testid="person-consent-card">
                <div className="mios-card-header">
                  <div className="mios-card-title flex items-center gap-2">
                    <Shield className="h-4 w-4 text-slate-400" />
                    {t.person.consent}
                  </div>
                  <ShieldCheck className="h-4 w-4 text-emerald-500" />
                </div>
                <div className="mios-card-body grid gap-2.5 sm:grid-cols-2">
                  {CONSENT_KEYS.map((k) => (
                    <label
                      key={k}
                      data-testid={`consent-${k}`}
                      className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                    >
                      <span className="text-sm text-slate-700">{t.person.consentKeys[k]}</span>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={!!selected.consent?.[k]}
                        onChange={(e) => toggleConsent(k, e.target.checked)}
                        disabled={saving}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="mios-card" data-testid="person-encounters-card">
                <div className="mios-card-header">
                  <div className="mios-card-title flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-slate-400" />
                    {t.person.recentEncounters}
                  </div>
                  <Smartphone className="h-4 w-4 text-slate-400" />
                </div>
                <div className="mios-card-body">
                  {encounters.length === 0 && <div className="text-sm text-slate-400">{t.person.noEncounters}</div>}
                  <ul className="space-y-2.5">
                    {encounters.slice(0, 15).map((e) => (
                      <li key={e.id} className="rounded-md border border-slate-200 p-2.5 text-sm">
                        <div className="flex items-center justify-between text-[11px] text-slate-500">
                          <span className="uppercase tracking-wide">{e.channel}</span>
                          <span>{new Date(e.occurred_at).toLocaleString(language)}</span>
                        </div>
                        {e.summary && <div className="mt-1 text-slate-700">{e.summary}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          ) : (
            <div className="mios-card">
              <div className="mios-card-body text-sm text-slate-500">{t.common.loading}</div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, onSave, disabled, testid, multiline = false }) {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  const dirty = v !== (value || "");
  return (
    <div className="grid gap-1.5">
      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="flex items-start gap-2">
        {multiline ? (
          <textarea
            data-testid={testid}
            value={v}
            onChange={(e) => setV(e.target.value)}
            className="mios-input min-h-[64px] flex-1"
          />
        ) : (
          <input
            data-testid={testid}
            value={v}
            onChange={(e) => setV(e.target.value)}
            className="mios-input flex-1"
          />
        )}
        <button
          type="button"
          disabled={!dirty || disabled}
          onClick={() => onSave(v)}
          className="mios-btn mios-btn-secondary"
          data-testid={`${testid}-save`}
        >
          ✓
        </button>
      </div>
    </div>
  );
}
